import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const LandingPage = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);

  // --- AUTH CHECK ---
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      // Redirect to dashboard after a fresh sign-in (e.g. OAuth callback landing here)
      // In Supabase v2, SIGNED_IN only fires for new sign-ins, not existing sessions
      if (_event === 'SIGNED_IN' && session) {
        navigate('/dashboard');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  // --- FETCH DEMO MODEL & HERO IMAGE ---
  const [demoModelUrl, setDemoModelUrl] = useState(null);

  // --- AR DEMO STATE ---
  const [isArActive, setIsArActive] = useState(false);
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const modelRef = React.useRef(null);

  const handleEnable3D = () => {
    // Dynamically import model-viewer only when user clicks (saves ~500KB on initial load)
    import('@google/model-viewer').then(() => {
      setIsArActive(true);
    });
  };

  useEffect(() => {
    if (isArActive && modelRef.current) {
      const model = modelRef.current;
      const handleLoad = () => setIsModelLoaded(true);

      model.addEventListener('load', handleLoad);
      model.addEventListener('poster-dismissed', handleLoad);

      if (model.loaded) setIsModelLoaded(true);

      return () => {
        model.removeEventListener('load', handleLoad);
        model.removeEventListener('poster-dismissed', handleLoad);
      };
    }
  }, [isArActive, demoModelUrl]);
  const [demoPosterUrl, setDemoPosterUrl] = useState("https://images.unsplash.com/photo-1600891964599-f61ba0e24092?auto=format&fit=crop&w=800&q=80"); // Default fallback

  useEffect(() => {
    const fetchDemoAssets = async () => {
      try {
        // Fetch from the specific table created by user
        const { data, error } = await supabase
          .from('heroImage_and_model')
          .select('heroimage, model')
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error("Error fetching hero assets:", error);
          return;
        }

        if (data) {
          if (data.model) setDemoModelUrl(data.model);
          if (data.heroimage) setDemoPosterUrl(data.heroimage);
        }
      } catch (err) {
        console.error("Unexpected error fetching demo assets:", err);
      }
    };

    fetchDemoAssets();
  }, []);

  // Smart Button Logic
  const handlePrimaryAction = () => {
    if (session) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="bg-background-light font-display text-neutral-dark overflow-x-hidden">

      {/* --- NAVBAR --- */}
      <div className="sticky top-0 z-50 w-full bg-[#fcf9f8]/90 backdrop-blur-md border-b border-[#f3eae7]">
        <div className="px-4 md:px-10 py-3 flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/favicon.png" alt="Logo" className="w-8 h-8 object-contain" />
            <h2 className="text-neutral-dark text-xl font-bold tracking-tight">TableAR</h2>
          </div>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/features" className="text-neutral-dark text-sm font-medium hover:text-primary transition-colors">Features</Link>
            <a className="text-neutral-dark text-sm font-medium hover:text-primary transition-colors" href="#benefits">Benefits</a>

            {/* UPDATED: Link to Pricing Page */}
            <button
              onClick={() => navigate('/pricing')}
              className="text-neutral-dark text-sm font-medium hover:text-primary transition-colors"
            >
              Pricing
            </button>

            {/* UPDATED: Link to About Page */}
            <Link to="/about" className="text-neutral-dark text-sm font-medium hover:text-primary transition-colors">
              About Us
            </Link>
          </div>

          <div className="flex items-center gap-4">
            {/* DYNAMIC NAV BUTTON */}
            <button
              onClick={handlePrimaryAction}
              className="hidden sm:flex bg-primary hover:bg-primary/90 text-white text-sm font-bold py-2.5 px-5 rounded-lg transition-all shadow-lg shadow-primary/20"
            >
              {session ? "Go to Dashboard" : "Get Started"}
            </button>
            <button className="md:hidden text-neutral-dark">
              ☰
            </button>
          </div>
        </div>
      </div>

      {/* --- HERO SECTION --- */}
      <section className="relative pt-12 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-10">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="flex-1 space-y-8 text-center lg:text-left z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 border border-orange-200 text-primary text-xs font-bold uppercase tracking-wider mb-2">
                <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
                Trusted by Restaurants Across India
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black leading-[1.1] tracking-tight text-neutral-dark">
                Increase Revenue with <span className="text-primary">Every Table Visit</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto lg:mx-0 font-normal leading-relaxed">
                TableAR lets guests visualize dishes in 3D before ordering — boosting average order value, reducing confusion, and creating a dining experience worth sharing.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={handlePrimaryAction}
                  className="flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white text-base font-bold h-12 px-8 rounded-lg shadow-xl shadow-primary/25 transition-all transform hover:-translate-y-0.5"
                >
                  {session ? "Go to Dashboard" : "Start Free Trial →"}
                </button>
                <button
                  onClick={() => window.location.href = 'https://www.tablear.in/menu/133b9cb1-60fc-4fe2-92ea-fabbc5e8fd59?table=A1'}
                  className="flex items-center justify-center gap-2 bg-white border border-gray-200 hover:border-primary/50 text-neutral-dark text-base font-bold h-12 px-8 rounded-lg transition-all"
                >
                  <span>▶</span> See Live Demo
                </button>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start pt-2">
                {['📱 No App Required', '⚡ Setup in 5 Minutes', '🌐 Works on Any Phone'].map((badge, i) => (
                  <span key={i} className="text-sm text-gray-500 font-medium flex items-center gap-1.5">✓ {badge}</span>
                ))}
              </div>

            </div>


            <div className="flex-1 w-full max-w-[600px] lg:max-w-none">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-100 bg-gray-100 aspect-[4/3] group cursor-pointer" onClick={handleEnable3D}>

                {/* 1. IDLE STATE: Image Overlay */}
                {!isArActive ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                      style={{ backgroundImage: `url('${demoPosterUrl}')` }}
                    ></div>
                    <div className="absolute inset-0 bg-black/10 transition-opacity group-hover:opacity-20"></div>

                    {/* Overlay Button */}
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-sm px-6 py-3 rounded-full flex items-center gap-2 shadow-lg transition-transform transform group-hover:scale-105 animate-pulse">
                      <span className="text-xl">📦</span>
                      <span className="font-bold text-gray-900 text-sm">View in 3D</span>
                    </div>
                  </div>
                ) : (
                  /* 2. ACTIVE STATE: 3D Model */
                  <>
                    {/* Dynamic Loading Spinner */}
                    <div className={`absolute inset-0 flex items-center justify-center bg-white z-10 transition-opacity duration-500 pointer-events-none ${isModelLoaded ? 'opacity-0' : 'opacity-100'}`}>
                      <div
                        className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
                        style={{ borderColor: '#ee5b2b', borderTopColor: 'transparent' }}
                      ></div>
                    </div>

                    <model-viewer
                      ref={modelRef}
                      src={demoModelUrl}
                      alt="3D Menu Demo"
                      ar
                      ar-modes="scene-viewer webxr quick-look"
                      camera-controls
                      auto-rotate
                      shadow-intensity="1"
                      style={{ width: '100%', height: '100%', backgroundColor: '#f3f4f6' }}
                    >
                      <button slot="ar-button" className="hidden"></button>
                    </model-viewer>

                    {/* Close Button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); setIsArActive(false); }}
                      className="absolute top-4 right-4 z-20 bg-white/80 rounded-full p-2 text-black hover:bg-red-500 hover:text-white transition-colors shadow-md"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>

                    {/* AR Camera Button */}
                    <button
                      className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 text-white px-6 py-3 rounded-full font-bold shadow-lg hover:opacity-90 flex items-center gap-2 transition-colors transform hover:scale-105"
                      style={{ backgroundColor: '#ee5b2b' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const model = modelRef.current;
                        if (model && model.canActivateAR) model.activateAR();
                      }}
                    >
                      <span>✨ View in AR</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- SOCIAL PROOF STRIP --- */}
      <section className="py-8 bg-white border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 flex flex-wrap justify-center gap-8 md:gap-16 items-center">
          {[
            { num: '50+', label: 'Restaurants Onboarded' },
            { num: '10,000+', label: 'AR Views Served' },
            { num: '4.8/5', label: 'Average Satisfaction' },
            { num: 'Pan-India', label: 'Presence' },
          ].map((stat, i) => (
            <div key={i} className="text-center">
              <p className="text-2xl md:text-3xl font-black text-neutral-dark">{stat.num}</p>
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* --- HOW IT WORKS --- */}
      <section className="py-24 bg-white relative" id="how-it-works">
        <div className="max-w-7xl mx-auto px-4 md:px-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-primary font-bold tracking-wide uppercase text-sm mb-3">Go Live in Under 10 Minutes</h2>
            <h3 className="text-3xl md:text-4xl font-bold text-neutral-dark mb-6">Three Steps to a Smarter Menu</h3>
            <p className="text-gray-600 text-lg">No developers required. Set up your interactive AR menu during a single coffee break.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-transparent via-orange-200 to-transparent z-0"></div>

            {/* Step 1 */}
            <div className="relative z-10 flex flex-col items-center text-center group">
              <div className="w-24 h-24 rounded-2xl bg-white border-2 border-orange-100 shadow-lg flex items-center justify-center mb-6 group-hover:border-primary transition-colors duration-300">
                <span className="text-4xl text-primary">📸</span>
              </div>
              <h4 className="text-xl font-bold mb-3 text-neutral-dark">1. Upload Your Dishes</h4>
              <p className="text-gray-500 leading-relaxed px-4">Add photos and 3D models of your dishes. Our platform handles the rest automatically.</p>
            </div>

            {/* Step 2 */}
            <div className="relative z-10 flex flex-col items-center text-center group">
              <div className="w-24 h-24 rounded-2xl bg-white border-2 border-orange-100 shadow-lg flex items-center justify-center mb-6 group-hover:border-primary transition-colors duration-300">
                <span className="text-4xl text-primary">✨</span>
              </div>
              <h4 className="text-xl font-bold mb-3 text-neutral-dark">2. Customize & Publish</h4>
              <p className="text-gray-500 leading-relaxed px-4">Set prices, descriptions, and categories. Your interactive menu goes live instantly.</p>
            </div>

            {/* Step 3 */}
            <div className="relative z-10 flex flex-col items-center text-center group">
              <div className="w-24 h-24 rounded-2xl bg-white border-2 border-orange-100 shadow-lg flex items-center justify-center mb-6 group-hover:border-primary transition-colors duration-300">
                <span className="text-4xl text-primary">🪧</span>
              </div>
              <h4 className="text-xl font-bold mb-3 text-neutral-dark">3. Place QR & Grow</h4>
              <p className="text-gray-500 leading-relaxed px-4">Print your QR code, place on tables, and watch engagement soar. Guests just scan — no app needed.</p>
            </div>
          </div>


        </div>
      </section>

      {/* --- BENEFITS --- */}
      <section className="py-24 bg-background-light" id="benefits">
        <div className="max-w-7xl mx-auto px-4 md:px-10">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <div className="flex-1 space-y-8">
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-dark leading-tight">
                Why Revenue-Focused Restaurants <br /> Choose TableAR
              </h2>
              <p className="text-gray-600 text-lg">
                Not a gimmick — a proven revenue lever. Restaurants using interactive menus report measurably higher order values and fewer operational issues.
              </p>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="mt-1 bg-green-100 p-2 rounded-lg h-fit text-green-600 text-xl">📈</div>
                  <div>
                    <h3 className="text-lg font-bold text-neutral-dark">Boost Average Order Value by 22%</h3>
                    <p className="text-gray-500 text-sm mt-1">When guests see a dish in 3D, they order more confidently — and add more appetizers, drinks, and desserts to their table.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="mt-1 bg-blue-100 p-2 rounded-lg h-fit text-blue-600 text-xl">✅</div>
                  <div>
                    <h3 className="text-lg font-bold text-neutral-dark">Eliminate Order Confusion</h3>
                    <p className="text-gray-500 text-sm mt-1">Guests see exactly what they're getting. Fewer wrong orders means lower food waste, faster table turns, and happier reviews.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="mt-1 bg-purple-100 p-2 rounded-lg h-fit text-purple-600 text-xl">📲</div>
                  <div>
                    <h3 className="text-lg font-bold text-neutral-dark">Turn Every Table Into a Marketing Channel</h3>
                    <p className="text-gray-500 text-sm mt-1">Guests share 3D food experiences on Instagram and WhatsApp organically. Your reach grows without spending on ads.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="mt-1 bg-amber-100 p-2 rounded-lg h-fit text-amber-600 text-xl">👑</div>
                  <div>
                    <h3 className="text-lg font-bold text-neutral-dark">Elevate Your Brand Perception</h3>
                    <p className="text-gray-500 text-sm mt-1">A 3D interactive menu positions your restaurant as innovative and premium — the kind of place guests talk about and return to.</p>
                  </div>
                </div>
              </div>

              <div className="pt-6">
                <button
                  onClick={handlePrimaryAction}
                  className="bg-neutral-dark hover:bg-black text-white text-base font-bold py-3 px-8 rounded-lg shadow-lg transition-transform hover:-translate-y-0.5"
                >
                  {session ? "Go to Dashboard" : "Start Free Trial"}
                </button>
              </div>
            </div>

            <div className="flex-1 w-full relative">
              <div className="relative w-full aspect-square max-w-[500px] mx-auto">
                <div className="absolute top-0 right-0 w-3/4 h-3/4 bg-orange-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>
                <div className="absolute bottom-0 left-0 w-3/4 h-3/4 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>
                <div className="relative z-10 grid grid-cols-2 gap-4 p-4">
                  <div className="bg-white p-3 rounded-2xl shadow-xl transform translate-y-8">
                    <div
                      className="aspect-[4/5] bg-cover bg-center rounded-xl mb-3"
                      style={{ backgroundImage: "url('https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=400&q=80')" }}
                    ></div>
                    <div className="h-2 w-2/3 bg-gray-100 rounded mb-2"></div>
                    <div className="h-2 w-1/3 bg-gray-100 rounded"></div>
                  </div>
                  <div className="bg-white p-3 rounded-2xl shadow-xl transform -translate-y-8">
                    <div
                      className="aspect-[4/5] bg-cover bg-center rounded-xl mb-3"
                      style={{ backgroundImage: "url('https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&w=400&q=80')" }}
                    ></div>
                    <div className="h-2 w-2/3 bg-gray-100 rounded mb-2"></div>
                    <div className="h-2 w-1/3 bg-gray-100 rounded"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- WHO IT'S FOR --- */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-10">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-primary font-bold tracking-wide uppercase text-sm mb-3">Purpose-Built for Hospitality</h2>
            <h3 className="text-3xl md:text-4xl font-bold text-neutral-dark mb-4">Designed for Businesses That Refuse to Be Average</h3>
            <p className="text-gray-600 text-lg">Whether you're a single-location cafe or a multi-property resort brand, TableAR adapts to your scale.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: '☕', title: 'Modern Cafes', desc: 'Elevate your brand from ordinary to Instagram-worthy. Let your specialty drinks sell themselves.' },
              { icon: '🍽️', title: 'Fine Dining Restaurants', desc: 'Set precise expectations for every course. Reduce send-backs, increase satisfaction scores.' },
              { icon: '🏨', title: 'Boutique Hotels', desc: 'Impress guests from the first meal. In-room dining becomes a premium, visual experience.' },
              { icon: '🏖️', title: 'Premium Resorts', desc: 'Multilingual, visual menus across pools, restaurants, and bars. No staff bottleneck.' },
              { icon: '🔗', title: 'Multi-Location Chains', desc: 'Consistent brand experience across every outlet. Centralized menu management from one dashboard.' },
              { icon: '📊', title: 'Data-Driven Operators', desc: 'Track which dishes get viewed, ordered, and shared. Make menu decisions backed by real data.' },
            ].map((item, i) => (
              <div key={i} className="bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:border-primary/30 hover:shadow-lg transition-all duration-300 group">
                <div className="text-3xl mb-3">{item.icon}</div>
                <h4 className="text-lg font-bold text-neutral-dark mb-2 group-hover:text-primary transition-colors">{item.title}</h4>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- DASHBOARD / ANALYTICS POSITIONING --- */}
      <section className="py-20 bg-background-light">
        <div className="max-w-5xl mx-auto px-4 md:px-10">
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xl overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <h2 className="text-primary font-bold tracking-wide uppercase text-sm mb-3">Beyond a Menu</h2>
                <h3 className="text-2xl md:text-3xl font-bold text-neutral-dark mb-4 leading-tight">A Restaurant Intelligence Platform</h3>
                <p className="text-gray-600 mb-6">TableAR isn't just about visuals. Every interaction generates data you can act on.</p>
                <ul className="space-y-3">
                  {[
                    'Track which dishes get the most 3D views',
                    'Measure conversion from view to order',
                    'Identify your most popular categories',
                    'Monitor real-time engagement analytics',
                    'Export reports for data-driven menu decisions',
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm">
                      <span className="w-5 h-5 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">✓</span>
                      <span className="text-gray-700 font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-8 md:p-12 flex items-center justify-center">
                <div className="w-full space-y-4">
                  <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm border border-white/10">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white/60 text-xs font-medium">Avg. Order Value</span>
                      <span className="text-green-400 text-xs font-bold">↑ 22%</span>
                    </div>
                    <p className="text-white text-2xl font-black">₹847</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm border border-white/10">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white/60 text-xs font-medium">3D Views Today</span>
                      <span className="text-green-400 text-xs font-bold">↑ 18%</span>
                    </div>
                    <p className="text-white text-2xl font-black">1,247</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm border border-white/10">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white/60 text-xs font-medium">Top Dish</span>
                      <span className="text-orange-400 text-xs font-bold">🔥 Trending</span>
                    </div>
                    <p className="text-white text-lg font-bold">Truffle Pasta</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- FAQ SECTION --- */}
      <section className="py-24 bg-white relative overflow-hidden">
        {/* Decorative Blob */}
        <div className="absolute top-0 left-0 w-full h-full text-gray-50 pointer-events-none -z-10 overflow-hidden">
          <svg className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-1/2 w-[800px] h-[800px]" fill="currentColor" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <path fill="#FFFAF0" d="M44.7,-76.4C58.9,-69.2,71.8,-59.1,81.6,-46.6C91.4,-34.1,98.1,-19.2,95.8,-4.9C93.5,9.4,82.2,23.1,70.8,34.5C59.4,45.9,47.9,55,35.4,62.3C22.9,69.6,9.4,75.1,-3.3,80.8C-16,86.5,-27.9,92.4,-38.7,87.7C-49.5,83,-59.2,67.7,-68.2,53.4C-77.2,39.1,-85.5,25.8,-86.3,11.8C-87.1,-2.2,-80.4,-16.9,-71.4,-29.4C-62.4,-41.9,-51.1,-52.2,-38.5,-59.9C-25.9,-67.6,-12,-72.7,2.3,-76.7C16.6,-80.7,33.2,-83.6,44.7,-76.4Z" transform="translate(100 100)" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left Column: Image/Visuals */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white transform rotate-2 hover:rotate-0 transition-transform duration-500">
                <div className="aspect-[4/5] bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1559339352-11d035aa65de?auto=format&fit=crop&w=800&q=80')" }}></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>

              {/* Floating Element */}
              <div className="absolute -bottom-10 -right-10 bg-white p-6 rounded-2xl shadow-xl border border-gray-100 hidden md:block max-w-[200px]">
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-green-100 text-green-600 p-2 rounded-lg">💬</div>
                  <span className="font-bold text-neutral-dark text-sm">24/7 Support</span>
                </div>
                <p className="text-xs text-gray-500">We're here to help you set up.</p>
              </div>
            </div>

            {/* Right Column: Content & FAQs */}
            <div>
              <div className="mb-10">
                <h2 className="text-primary font-bold tracking-wide uppercase text-sm mb-3">Common Questions</h2>
                <h3 className="text-4xl font-black text-neutral-dark mb-4 leading-tight">Everything You Need to <span className="text-primary">Decide</span></h3>
                <p className="text-gray-600 text-lg">Honest answers to the questions every restaurant owner asks before getting started.</p>
              </div>

              <div className="space-y-6">
                {[
                  { q: 'Is it complicated to set up?', a: 'Not at all. Upload your menu items, set prices, and print your QR code. No developers, no training manuals, no IT team required.' },
                  { q: 'Will my staff need training?', a: 'Zero training needed. Your staff continues operating as usual. Guests simply scan a QR code from their own phone — the experience is entirely self-serve for the customer.' },
                  { q: 'Does AR work on all phones?', a: 'Yes. TableAR works on any modern smartphone browser — iPhone and Android. No app download required. If a phone can open a website, it can view your 3D menu.' },
                  { q: 'Can I use my own branding?', a: 'Absolutely. Add your logo, brand colors, and custom categories. Your AR menu looks and feels like an extension of your restaurant — not a third-party tool.' },
                  { q: 'What happens if I cancel?', a: 'Cancel anytime — no penalties, no lock-in contracts. Your data stays yours. We believe you\'ll stay because of results, not because of fine print.' },
                ].map((faq, i) => (
                  <div key={i} className="group">
                    <h4 className="text-lg font-bold text-neutral-dark mb-2 flex items-center gap-3">
                      <span className="bg-orange-100 text-primary w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">{i + 1}</span>
                      {faq.q}
                    </h4>
                    <p className="text-gray-600 leading-relaxed pl-11 border-l-2 border-orange-100 ml-4 group-hover:border-primary transition-colors">
                      {faq.a}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <a
                  href="https://mail.google.com/mail/?view=cm&fs=1&to=pator.explore.tech@gmail.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-primary hover:text-primary/80 transition-colors flex items-center gap-2"
                >
                  Have a different question? We respond within 2 hours <span aria-hidden="true">&rarr;</span>
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* --- VALUE FRAMING SECTION --- */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-10">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-primary font-bold tracking-wide uppercase text-sm mb-3">The Smart Investment</h2>
            <h3 className="text-3xl md:text-4xl font-bold text-neutral-dark mb-4">Why Most Restaurants Overspend on Tech</h3>
            <p className="text-gray-600 text-lg">Custom development costs lakhs and takes months. TableAR delivers more — ready to use today, at a predictable monthly cost.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Traditional Approach */}
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8">
              <h4 className="text-lg font-bold text-gray-400 uppercase tracking-wider mb-6">❌ Traditional Approach</h4>
              <div className="space-y-4">
                {[
                  { item: 'Custom ordering website', cost: '₹20,000 – ₹1,00,000' },
                  { item: 'AR & 3D development', cost: '₹50,000 – ₹2,00,000' },
                  { item: 'Monthly hosting & updates', cost: '₹2,000 – ₹5,000/mo' },
                  { item: 'Developer on retainer', cost: '₹10,000+/mo' },
                  { item: 'Timeline to launch', cost: '3 – 6 months' },
                ].map((row, i) => (
                  <div key={i} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                    <span className="text-gray-600 text-sm">{row.item}</span>
                    <span className="text-red-500 font-bold text-sm">{row.cost}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-4 border-t-2 border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-gray-600">Total estimated cost</span>
                  <span className="text-red-500 font-black text-xl">₹1,00,000+</span>
                </div>
              </div>
            </div>

            {/* TableAR Approach */}
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-primary/30 rounded-2xl p-8 relative">
              <div className="absolute -top-3 right-6">
                <span className="bg-primary text-white text-xs font-bold px-4 py-1 rounded-full shadow-lg">RECOMMENDED</span>
              </div>
              <h4 className="text-lg font-bold text-primary uppercase tracking-wider mb-6">✓ TableAR — All-in-One</h4>
              <div className="space-y-4">
                {[
                  'Interactive 3D & AR menu',
                  'Live ordering system',
                  'Engagement analytics dashboard',
                  'QR code generation',
                  'Custom branding',
                  'Go live instantly',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 py-1">
                    <span className="w-5 h-5 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">✓</span>
                    <span className="text-gray-700 text-sm font-medium">{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-4 border-t-2 border-primary/20">
                <div className="flex justify-between items-center mb-4">
                  <span className="font-bold text-neutral-dark">Starting at</span>
                  <div className="text-right">
                    <span className="text-primary font-black text-3xl">₹499</span>
                    <span className="text-gray-500 text-sm font-medium">/month + GST</span>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/pricing')}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-3 rounded-lg shadow-lg shadow-primary/20 transition-all transform hover:-translate-y-0.5"
                >
                  View All Plans →
                </button>
              </div>
            </div>
          </div>

          {/* Risk Reduction */}
          <div className="flex flex-wrap justify-center gap-6 md:gap-10">
            {['🔓 No Credit Card Required', '↩️ Cancel Anytime', '🚀 Scale as You Grow', '🛡️ Zero Setup Fee'].map((item, i) => (
              <span key={i} className="text-sm text-gray-500 font-medium">{item}</span>
            ))}
          </div>
        </div>
      </section>

      {/* --- FINAL CTA BANNER --- */}
      <section className="py-24 relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5"></div>
        {/* Glow effect */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/20 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <p className="text-primary font-bold text-sm uppercase tracking-widest mb-6">The Future Is Already Here</p>
          <h2 className="text-3xl md:text-5xl font-black text-white mb-6 leading-tight">
            Don't Let Your Competitors <br className="hidden md:block" />Modernize First
          </h2>
          <p className="text-gray-400 text-xl mb-10 font-medium max-w-2xl mx-auto leading-relaxed">
            Every day without TableAR is a day your competitors could get ahead. Start your free trial and see the difference in your first week.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={handlePrimaryAction}
              className="bg-primary hover:bg-primary/90 text-white text-xl font-bold py-4 px-12 rounded-full shadow-2xl shadow-primary/30 transition-all transform hover:scale-105"
            >
              Start Free Trial — No Card Needed
            </button>
            <a
              href="https://mail.google.com/mail/?view=cm&fs=1&to=pator.explore.tech@gmail.com&su=Book%20a%20Demo%20-%20TableAR"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white font-bold border-2 border-white/20 hover:border-white/40 hover:bg-white/5 px-8 py-4 rounded-full transition-all"
            >
              Book a Personalized Demo
            </a>
          </div>
          <p className="mt-8 text-gray-500 text-sm font-medium">Free plan available • No credit card required • Go live instantly • Cancel anytime</p>
        </div>
      </section>

      {/* --- FOOTER --- */}
      <footer className="bg-white border-t border-gray-100 pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <img src="/favicon.png" alt="Logo" className="w-6 h-6 object-contain" />
                <h2 className="text-xl font-bold text-neutral-dark">TableAR</h2>
              </div>
              <p className="text-sm text-gray-500">India's leading AR-powered menu and ordering platform for forward-thinking restaurants, cafes, and hotels.</p>
            </div>
            <div>
              <h3 className="font-bold text-neutral-dark mb-4">Product</h3>
              <ul className="space-y-3 text-sm text-gray-500">
                <li><Link className="hover:text-primary transition-colors" to="/features">Features</Link></li>
                {/* UPDATED: Link to Pricing Page */}
                <li><Link to="/pricing" className="hover:text-primary transition-colors">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-neutral-dark mb-4">Company</h3>
              <ul className="space-y-3 text-sm text-gray-500">
                {/* UPDATED: Company Links */}
                <li><Link className="hover:text-primary transition-colors" to="/about">About Us</Link></li>
                <li><a href="https://mail.google.com/mail/?view=cm&fs=1&to=pator.explore.tech@gmail.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-neutral-dark mb-4">Follow Our Journey</h3>
              <p className="text-sm text-gray-500 mb-4">Join us as we build the future of immersive dining.</p>

              <a
                href="https://www.instagram.com/tablear.in/"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-white hover:shadow-lg transition-all duration-300 hover:border-pink-200"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg group-hover:scale-110 transition-transform">
                  📸
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-neutral-dark text-sm group-hover:text-pink-600 transition-colors">@tablear.in</span>
                  <span className="text-xs text-gray-400">Follow on Instagram &rarr;</span>
                </div>
              </a>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-500">
            <p>© 2026 ARMenu Inc. All rights reserved.</p>
            <div className="flex gap-6">
              {/* UPDATED: Legal Links */}
              <Link className="hover:text-neutral-dark transition-colors" to="/privacy">Privacy Policy</Link>
              <Link className="hover:text-neutral-dark transition-colors" to="/terms">Terms of Service</Link>
              <Link className="hover:text-neutral-dark transition-colors" to="/refund">Refund Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div >
  );
};

export default LandingPage;